import { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";

import * as dashboard from "../../services/dashboard";
import { CustomNotification } from "../../util/CustomNotification";
import { RootState } from "../../appRedux/store";
import useValue from "../../hooks/useValue";
import { ParkingRequestTypeConstant } from "../../constants/DatabaseConstants/ParkingConstants";
import { useRefetchAPIShortPolling } from "../../hooks/useRefetchAPIShortPolling";
import { ParkingReportUtil } from "../../util/ParkingReportUtil";

import type { DashboardDataInterface, MaintenanceNotificationData, NotificationMessage, ParkingStatusData, VehicleTypeParkingData } from "../../shared/interface/dashboard.interface";
import type FixMeLater from "../../shared/types/fixMeLater.type";

export const useDashboard = () => {
  /* STATE */
  const [dashboardData, setDashboardData] = useState<DashboardDataInterface | null | FixMeLater>(null);
  const isFirstRender = useRef(true);


  /* CUSTOM HOOKS */
  const { value: loading, handleDisableValue: stopLoading, handleEnableValue: startLoading } = useValue(false);
  const { masterFilters } = useSelector(({ common }: RootState) => common);

  /* 10 sec short polling */
  const shortPolling = 10000;
  const { shortPollingRefetch } = useRefetchAPIShortPolling(shortPolling);

  /* HANDLERS */

  const mergeData = (keyFobData: ParkingStatusData[], vehicleData: VehicleTypeParkingData[]) => {
    if (!keyFobData && !vehicleData) return null;
    /* Group keyFobData by vehicleTypeName */
    const groupedData = keyFobData?.reduce<Record<string, ParkingStatusData[]>>((groups, keyFob) => {
      (groups[keyFob?.vehicleTypeName] ||= []).push(keyFob);
      return groups;
    }, {});

    /* Map vehicleData by name for quick access */
    const vehicleDataMap = Object?.fromEntries(vehicleData?.map((vehicle) => [vehicle?.name, vehicle]));

    /* Create MergedData array based on groupedData and vehicleDataMap */
    return Object?.entries(groupedData)?.map(([vehicleTypeName, keyfobList], index) => {
      const vehicle = vehicleDataMap[vehicleTypeName];
      return {
        id: vehicle?.id || index + 1,
        total_slots: vehicle?.total_slots || 0,
        vehicle_type_id: vehicle?.vehicle_type_id || 0,
        total_parked_slots: vehicle?.total_parked_slots || 0,
        total_empty_slots: vehicle?.total_empty_slots || 0,
        total_dead_slots: vehicle?.total_dead_slots || 0,
        total_booked_slots: vehicle?.total_booked_slots || 0,
        name: vehicleTypeName,
        keyfobList
      };
    });
  };

  const formattedMaintenanceNotification = (data?: MaintenanceNotificationData) => {
    if (!data) return [];

    const { todayNotifications = [], futureNotifications = [] } = data;

    const formatNotification = (notifications: NotificationMessage[], isToday: boolean) =>
      notifications.map((item) => ({
        ...item,
        isToday
      }));

    return [...formatNotification(todayNotifications, true), ...formatNotification(futureNotifications, false)];
  };

  // Format dashboard data
  const formattedDashboardData = useCallback(
    (data: DashboardDataInterface) => ({
      ...data,
      ongoingStatusData:
        data.ongoingStatusData?.map((item) => ({
          ...item,
          storage: item.requestType === ParkingRequestTypeConstant.STORAGE ? item.keyFobToken : "-",
          retrieving: item.requestType === ParkingRequestTypeConstant.RETRIEVAL ? item.keyFobToken : "-"
        })) ?? [],
      todaysVisitorsData: ParkingReportUtil.formatParkingHistoryList(data?.todaysVisitorsData) ?? [],
      vehicleTypeParkingStatusData: mergeData(data.parkingStatusData, data.vehicleTypeParkingData),
      notificationList: formattedMaintenanceNotification(data?.notificationData)
    }),
    []
  );

  const fetchAllData = async (customerId: number, locationId: number, isLoading: boolean) => {
    try {
      if (isLoading) startLoading();
      const dashboardResponse = await dashboard.getDashboardDataByCustomerAndLocationId(customerId, locationId);
      const formattedData = formattedDashboardData(dashboardResponse?.data ?? null);
      setDashboardData(formattedData);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      stopLoading();
    }
  };

  /* EFFECTS */
  useEffect(() => {
    if (masterFilters?.customerId && masterFilters?.locationId) {
      fetchAllData(masterFilters?.customerId, masterFilters?.locationId, true);
    }
  }, [masterFilters?.customerId, masterFilters?.locationId]);

  /* shortPollingRefetch */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (masterFilters?.customerId && masterFilters?.locationId) {
      fetchAllData(masterFilters?.customerId, masterFilters?.locationId, false);
    }
  }, [shortPollingRefetch]);

  return {
    loading,
    dashboardData
  };
};
